import React, { useEffect, useState, useRef } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import "../Style/Dashboard.css";
import "../Style/DebitNote.css";
import "../Style/CreateDebitNote.css";
import "../Style/DownloadPDF.css";
import { useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { BASE_URL } from "../Apis/apiConfig";
import {Eye, Download, X, RefreshCw, FileText, ChevronLeft, ChevronRight, Search, AlertCircle, Building, User, Calendar, MapPin, FileX, Printer, DollarSign, FileSpreadsheet, ReceiptText, CreditCard as CreditCardIcon, BadgeDollarSign,
} from "lucide-react";
import Swal from "sweetalert2";

const DownloadPDF = () => {
  const { state } = useLocation();
  const memberName = state?.memberName.replace(/\./g, "") || "Unknown Member";
  const wingFlat = state?.unitNo || "N/A";
  const localSocietyName = localStorage.getItem("society") || "";
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedBill, setSelectedBill] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [ledger, setLedger] = useState([])
  const [viewMode, setViewMode] = useState("bills");
  const [loading, setLoading] = useState(false);
  const [loadingSociety, setLoadingSociety] = useState(false);
  const [societyData, setSocietyData] = useState(null);
  const [modalType, setModalType] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [summaryStats, setSummaryStats] = useState({ totalAmount: 0, totalItems: 0, pendingAmount: 0, paidAmount: 0, });

  const billRef = useRef(null);
  const [ledgerList, setLedgerList] = useState([])
  const [rows, setRows] = useState([]);

  const commonParams = { wingFlat, memberName, societyName: localSocietyName };
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  const fetchBillsForMember = async () => {
    setViewMode("bills");
    setError(null);
    setItems([]);
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await axios.get(`${BASE_URL}/api/get-bill-member`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: commonParams,
        validateStatus: null,
      });
      if (resp.status === 200 && Array.isArray(resp.data)) {
        setItems(resp.data);
        setPage(1);
        updateSummaryStats(resp.data, "bills");
      } else {
        setError(
          resp.data?.message || `Failed to fetch bills (status ${resp.status})`,
        );
      }
    } catch (err) {
      console.error(err);
      setError("Network or server error while fetching bills");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchLedgerForMember = async () => {
    setViewMode("ledger");
    setError(null);
    setItems([]);
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = commonParams;
      const [openingResp, billsResp, receiptsResp, ledgerResp] =
        await Promise.all([
          axios.post(`${BASE_URL}/opening/get-opening-by-member`, null, {
            headers,
            params,
            validateStatus: null,
          }),
          axios.get(`${BASE_URL}/api/get-bill-member`, {
            headers,
            params,
            validateStatus: null,
          }),
          axios.post(`${BASE_URL}/api/get-receipt-by-member`, null, {
            headers,
            params,
            validateStatus: null,
          }),
        ]);

      const openingArr =
        openingResp && openingResp.status === 200
          ? Array.isArray(openingResp.data)
            ? openingResp.data
            : [openingResp.data]
          : [];
      const billsArr =
        billsResp && billsResp.status === 200 && Array.isArray(billsResp.data)
          ? billsResp.data
          : [];
      const receiptsArr =
        receiptsResp &&
          receiptsResp.status === 200 &&
          Array.isArray(receiptsResp.data)
          ? receiptsResp.data
          : [];
      const ledgerArr =
        ledgerResp &&
          ledgerResp.status === 200 &&
          Array.isArray(ledgerResp.data)
          ? ledgerResp.data
          : [];
      const normalizedOpening = openingArr.map(normalizeOpening);
      const normalizedBills = billsArr.map(normalizeBill);
      const normalizedReceipts = receiptsArr.map(normalizeReceipt);
      const normalizedLedger = ledgerArr.map(normalizeLedger);
      const others = [
        ...normalizedLedger,
        ...normalizedBills,
        ...normalizedReceipts,
      ];

      const dateValue = (r) => {
        const d = r.date ? new Date(r.date) : null;
        return d && !isNaN(d) ? d.getTime() : 0;
      };
      others.sort(
        (a, b) =>
          dateValue(a) - dateValue(b) ||
          String(a.referenceNo || "").localeCompare(
            String(b.referenceNo || ""),
          ),
      );
      const final = [...normalizedOpening, ...others];
      setItems(final);
      setPage(1);
      updateSummaryStats(final, "ledger");
    } catch (err) {
      console.error("Error fetching combined ledger data:", err);
      setError(
        "Network or server error while fetching ledger and related data",
      );
    } finally {
      setLoading(false);
    }
  };

  function formatAmountForBill(v) {
    if (v === null || v === undefined || v === "") return "0.00";
    const cleaned = String(v)
      .replace(/(Dr|Cr)/gi, "")
      .replace(/,/g, "")
      .trim();
    const n = Number(cleaned);
    if (Number.isNaN(n)) return String(v);
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  useEffect(() => {
    const fetchAdminLedger = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axios.post(
          `${BASE_URL}/ledger/get-ledger-by-admin`,
          null,
          {
            params: {
              wingFlat: commonParams.wingFlat,
              memberName: commonParams.memberName,
              societyName: commonParams.societyName,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        let ledgerRows = [];

        if (Array.isArray(response.data)) {
          ledgerRows = response.data.map((r) => {
            const balanceValue = Number(r.balance || 0);

            return {
              date: r.ledgerDate ? new Date(r.ledgerDate) : null,
              referenceType: r.vchType,
              referenceNo: r.flatNo,
              narration: r.particular,
              chequeNo: null,
              chequeDate: null,
              __dr: r.debit ? Number(r.debit) : 0,
              __cr: r.credit ? Number(r.credit) : 0,
              totalAmount: balanceValue,
              __balanceLabel: `${balanceValue} Cr`,
              advancePayment: r.advancePayment,
              instrument: r.instrument,
              instrumentNo: r.instrumentNo
            };
          });
        }

        console.table(ledgerRows);
        setRows(ledgerRows);

      } catch (err) {
        console.error("Error fetching ledger:", err);
        setError(err.message);
      }
    };

    if (commonParams?.wingFlat && commonParams?.memberName) {
      fetchAdminLedger();
    }

  }, [
    commonParams.wingFlat,
    commonParams.memberName,
    commonParams.societyName
  ]);

  const getMonthYearKey = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return `${d.getMonth()}-${d.getFullYear()}`;
  };

  const fetchReceiptsForMember = async () => {
    setViewMode("receipts");
    setError(null);
    setItems([]);
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await axios.post(
        `${BASE_URL}/api/get-receipt-by-member`,
        null,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: commonParams,
          validateStatus: null,
        },
      );
      if (resp.status === 200 && Array.isArray(resp.data)) {
        setItems(resp.data);
        setPage(1);
        updateSummaryStats(resp.data, "receipts");
      } else {
        setError(
          resp.data?.message ||
          `Failed to fetch receipts (status ${resp.status})`,
        );
      }
    } catch (err) {
      console.error(err);
      setError("Network or server error while fetching receipts");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCreditForMember = async () => {
    setViewMode("credit");
    setError(null);
    setItems([]);
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await axios.post(
        `${BASE_URL}/api/get-credit-by-member`,
        null,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: commonParams,
          validateStatus: null,
        },
      );
      if (resp.status === 200 && Array.isArray(resp.data)) {
        const normalized = resp.data.map((row) => ({
          Bill_No:
            row.bill_no ??
            row.bill_no ??
            row.reference ??
            row.Reference ??
            null,
          Member_Name:
            row.member_name ?? row.MemberName ?? row.member_name ?? "",
          unit_no:
            row.unit_no ??
            row.UnitNO ??
            row.UnitNo ??
            row.Unit ??
            row.Unit_No ??
            "",
          reference: row.reference ?? row.Reference ?? row.ref ?? "",
          Date: row.date ?? row.VoucherDate ?? row.date ?? row.bill_date ?? "",
          non_occupancy:
            row.non_occupancy ??
            row.non_occupancy_Charges ??
            row.non_occupancyCharges ??
            null,
          credit_amount:
            row.credit_amount ?? row.CreditAmount ?? row.Credit ?? null,
          debit_amount:
            row.Debit_Amount ?? row.DebitAmount ?? row.Debit ?? null,
          Narration: row.narration ?? row.Remarks ?? row.Remark ?? "",
          raw: row,
        }));
        setItems(normalized);
        setPage(1);
        updateSummaryStats(normalized, "credit");
      } else {
        setError(
          resp.data?.message ||
          `Failed to fetch credit (status ${resp.status})`,
        );
      }
    } catch (err) {
      console.error(err);
      setError("Network or server error while fetching credit");
    } finally {
      setLoading(false);
    }
  };
  const EXCLUDED_DEBIT_KEYS = ["id", "bill_no", "unit_no", "member_name", "reference", "bill_amount", "credit_amount", "debit_amount", "sub_total", "date"];
  const isValidAmount = (val) =>
    val !== null && val !== "" && !isNaN(val) && Number(val) > 0;
  const debitChargeKeys = React.useMemo(() => {
    if (!items || items.length === 0) return [];
    return Object.keys(items[0]).filter(
      (key) =>
        !EXCLUDED_DEBIT_KEYS.includes(key) &&
        items.some((row) => isValidAmount(row[key])),
    );
  }, [items]);
  const formatLabel = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  const fetchDebitForMember = async () => {
    setViewMode("debit");
    setError(null);
    setItems([]);
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await axios.post(
        `${BASE_URL}/api/get-debit-by-member`,
        null,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: commonParams,
          validateStatus: null,
        },
      );
      if (resp.status === 200 && Array.isArray(resp.data)) {
        setItems(resp.data);
        setPage(1);
        updateSummaryStats(resp.data, "debit");
      } else {
        setError(
          resp.data?.message || `Failed to fetch debit (status ${resp.status})`,
        );
      }
    } catch (err) {
      console.error(err);
      setError("Network or server error while fetching debit");
    } finally {
      setLoading(false);
    }
  };
  const updateSummaryStats = (data, viewType) => {
    if (!Array.isArray(data) || data.length === 0) {
      setSummaryStats({
        totalAmount: 0,
        totalItems: 0,
        pendingAmount: 0,
        paidAmount: 0,
      });
      return;
    }

    let totalAmount = 0;
    let pendingAmount = 0;
    let paidAmount = 0;

    switch (viewType) {
      case "bills":
        data.forEach((item) => {
          const amount = numericVal(item.total_amount || 0);
          totalAmount += amount;
          pendingAmount += amount;
        });
        break;
      case "receipts":
        data.forEach((item) => {
          const amount = numericVal(item.amount || item.Amount || 0);
          totalAmount += amount;
          paidAmount += amount;
        });
        break;
      case "credit":
        data.forEach((item) => {
          const amount = numericVal(item.credit_amount || 0);
          totalAmount += amount;
        });
        break;
      case "debit":
        data.forEach((item) => {
          const amount = numericVal(item.debit_amount || 0);
          totalAmount += amount;
        });
        break;
      default:
        data.forEach((item) => {
          const dr = numericVal(item.Debit_Amount || 0);
          const cr = numericVal(item.Credit_Amount || 0);
          totalAmount += dr + cr;
        });
    }

    setSummaryStats({
      totalAmount,
      totalItems: data.length,
      pendingAmount,
      paidAmount,
    });
  };
  
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    const sortedItems = [...filteredData].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];
      if (key.includes("date") || key.includes("Date")) {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }
      if (typeof aValue === "number" && typeof bValue === "number") {
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      aValue = String(aValue || "").toLowerCase();
      bValue = String(bValue || "").toLowerCase();

      if (direction === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    setItems(sortedItems);
  };

  const numericVal = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const cleaned = String(v)
      .replace(/(Dr|Cr)/gi, "")
      .replace(/,/g, "")
      .trim();
    const n = Number(cleaned);
    return Number.isNaN(n) ? 0 : n;
  };


  function normalizeOpening(row) {
    return {
      type: "OPENING",
      date: row.Date ?? row.date ?? row.OpeningDate ?? null,
      referenceNo: row.reference_no ?? row.ref ?? "",
      narration:
        row.narration ?? row.remark ?? row.remarks ?? "Opening Balance",
      chequeNo: row.cheque_no ?? row.chequeNo ?? "",
      chequeDate: row.cheque_date ?? row.chequeDate ?? null,
      total: numericVal(row.total ?? row.Debit ?? row.total ?? 0),
      Credit_Amount: numericVal(row.total ?? row.total ?? row.total ?? 0),
      raw: row,
    };
  }
  function normalizeBill(row) {
    const normalized = { ...row };
    Object.keys(normalized).forEach((key) => {
      const val = normalized[key];
      if (!isNaN(val) && val !== null && val !== "") {
        normalized[key] = Number(val);
      }
    });
    return normalized;
  }
  function normalizeReceipt(row) {
    const cr = row.amount ?? row.AmountPaid ?? row.credit_amount ?? row.credit ?? row.CreditAmount ?? 0;
    return {
      type: "RECEIPT",
      date: row.receipt_date ?? row.Date ?? row.date ?? row.ReceiptDate ?? null,
      referenceType: "Receipt", referenceNo: row.receipt_no ?? row.ReceiptNo ?? row.Ref ?? row.reference ?? "",
      chequeNo: row.instrument_no ?? row.cheque_no ?? row.chequeNo ?? "", chequeDate:
        row.instrument_date ?? row.cheque_date ?? row.chequeDate ?? null, debit_amount: 0,
      credit_amount: numericVal(cr),
      raw: row,
      instrument: row.instrument,
    };
  }

  function normalizeLedger(row) {
    return {
      type: row.type ?? row.EntryType ?? "LEDGER",
      date: row.date ?? row.Date ?? row.TransactionDate ?? row.VoucherDate ?? row.Instrument_Date ?? null,
      referenceType: row.ReferenceType ?? row.referenceType ?? row.Particulars ?? row.Particular ?? "",
      referenceNo: row.reference_no ?? row.Ref ?? row.reference ?? row.Bill_No ?? "",
      narration: row.narration ?? row.Remarks ?? row.Description ?? "",
      chequeNo: row.cheque_no ?? row.Instrument_No ?? row.chequeNo ?? "",
      chequeDate: row.cheque_date ?? row.Instrument_Date ?? row.chequeDate ?? null,
      Debit_Amount: numericVal(row.debit_amount ?? row.Debit ?? row.drAmount ?? 0,),
      Credit_Amount: numericVal(row.credit_amount ?? row.Credit ?? row.crAmount ?? 0,), raw: row,
      advancePayment: numericVal(row.advancePayment ?? row.advancePayment),
      instrumentNo: row.instrumentNo,
      instrument: row.instrument
    };
  }

  const handleView = (item) => {
    setSelectedBill(item);
    setViewMode("billDetail");
    setModalOpen(true);
  };


  const handleViewCredit = (row, mode = "credit") => {
    const r = row?.raw ?? row ?? {};
    const get = (keys) => {
      for (const k of keys) {
        if (r[k] !== undefined && r[k] !== null && String(r[k]).trim() !== "")
          return r[k];
      }
      return "";
    };

    const excludeFields = [
      "id",
      "is_previous_year",
      "unit_no",
      "voucher_no",
      "credit_date",
      "debit_date",
      "member_name"
    ];
    const particulars = Object.keys(r)
      .filter((key) => !excludeFields.includes(key))
      .map((key) => {
        const value = r[key];

        if (value === null || value === undefined || String(value).trim() === "") {
          return null;
        }

        const num = numericVal(value);

        const label = key
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());

        return {
          desc: label,
          valueRaw: value,
          dr: mode === "debit" && num > 0 ? formatApiAmount(num) : "",
          cr: mode === "credit" && num > 0 ? formatApiAmount(num) : "",
        };
      })
      .filter(Boolean);


    if (particulars.length === 0) {
      const billAmountRaw = get(["bill_amount", "BillNo", "bill_amount"]);
      const waterRaw = get(["water_charges", "Water_Charges"]);
      if (String(waterRaw).trim() !== "") {
        particulars.push({
          desc: "Water Charges",
          valueRaw: waterRaw,
          dr: mode === "debit" ? formatApiAmount(waterRaw) : "",
          cr: mode === "credit" ? formatApiAmount(waterRaw) : "",
        });
      }
      if (String(billAmountRaw).trim() !== "") {
        particulars.push({
          desc: "Bill Amount",
          valueRaw: billAmountRaw,
          dr: mode === "debit" ? formatApiAmount(billAmountRaw) : "",
          cr: mode === "credit" ? formatApiAmount(billAmountRaw) : "",
        });
      }
    }

    const sum = particulars.reduce((s, p) => s + numericVal(p.valueRaw), 0);
    const totalDr = mode === "debit" ? sum : 0;
    const totalCr = mode === "credit" ? sum : 0;
    const creditNote = {
      VoucherNo: get(["voucher_no", "bill_no", "reference", "BillNo"]) || "—",

      Date: formatDate(
        get(["credit_date", "debit_date", "date", "Date"]) ||
        r.created_at ||
        r.createdAt
      ),
      SocietyName: get(["SocietyName", "societyName"]) || "Society",
      RegistrationNo: get(["RegistrationNo", "registrationNo"]) || "",
      HeaderAddress: "",
      ContactPerson: "",
      ContactPhone: "",
      Email: "",
      particulars,
      totalDr,
      totalCr,
      narration: get(["Narration", "narration", "Remarks", "Remark"]) || "",
      MemberName: get(["member_name", "MemberName"]) || "",
      UnitNo: get(["unit_no", "UnitNo"]) || "",
      rawRow: r,
      credit_Amount:
        mode === "credit"
          ? get(["credit_amount", "CreditAmount", "Credit"]) || ""
          : "",
      debit_Amount:
        mode !== "credit"
          ? get(["debit_amount", "DebitAmount", "Debit"]) || ""
          : "",
    };
    setSelectedBill(creditNote);
    setViewMode(mode);
    setModalOpen(true);
  };

  const society = data?.[0] || {};
  const notes = society?.notes || [];

  const fetchData = async () => {
    try {
      setLoading(true);

      const societyName = localStorage.getItem("society");
      const token = localStorage.getItem("token");

      const res = await axios.post(
        `${BASE_URL}/society-setting/get-society-setting`,
        null,
        {
          params: { societyName },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setData(res.data || []);
      console.log("guideline api:", res.data);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDebit = (row) => {
    const r = row?.raw ?? row ?? {};
    const particulars = Object.keys(r)
      .filter(

        (key) => key !== "voucher_no" &&
          !EXCLUDED_DEBIT_KEYS.includes(key) && isValidAmount(r[key]),
      )
      .map((key) => ({
        desc: formatLabel(key),
        valueRaw: r[key],
        dr: formatApiAmount(Number(r[key])),
        cr: "",
      }));

    const totalVal = particulars.reduce(
      (sum, p) => sum + Number(p.valueRaw),
      0,
    );
    const debitNote = {
      VoucherNo: r.voucher_no,
      Date: formatDate(r.debit_date),
      SocietyName: localStorage.getItem("society") || "Society",
      MemberName: r.member_name || memberName,
      UnitNo: r.unit_no || wingFlat,

      particulars: particulars.length
        ? particulars
        : [
          {
            desc: "Total Amount",
            dr: formatApiAmount(totalVal),
            cr: "",
          },
        ],

      totalDr: totalVal,
      totalCr: 0,
      narration: r.reference || "",
    };

    setSelectedBill(debitNote);
    setViewMode("debit");
    setModalOpen(true);
  };
  const handleViewReceipt = (receiptRow) => {
    const society = getSocietyField(
      "societyName",
      localSocietyName || "Society",
    );
    const numAmount = Number(
      receiptRow?.total_amount ||
      receiptRow?.AmountPaid ||
      receiptRow?.amount ||
      0,
    );
    const amountWords = numberToWords(numAmount);
    const amount =
      receiptRow?.total_amount ||
      receiptRow?.AmountPaid ||
      receiptRow?.amount ||
      0;
    const receiptObj = {
      ReceiptNo: receiptRow?.receipt_no || receiptRow?.ReceiptNo || 0,
      Date:
        receiptRow?.receipt_date ||
        receiptRow?.Date ||
        receiptRow?.date ||
        "",
      MemberName: receiptRow?.member_name || memberName || "",
      UnitNo: receiptRow?.unit_no || receiptRow?.UnitNo || wingFlat || "",
      Amount: Number(amount),
      AmountWords: amountWords,
   
      InstrumentNo: receiptRow?.instrumentNo || receiptRow?.Cheque_No || "",
      InstrumentDate: receiptRow?.instrument || receiptRow?.Date || "",
      Online: receiptRow?.Online || "No",
      DrawnOn: receiptRow?.BankName || receiptRow?.DrawnOn || "",
      SocietyName: society,
      RegistrationNo: getSocietyField(
        "societyRegistrationNo",
        receiptRow?.RegistrationNo || "",
      ),
      HeaderAddress: getFullAddress() || receiptRow?.HeaderAddress || "",
      Note: receiptRow?.Note || "",
      ContactPhone: getSocietyField("contactPersonPhoneNo", ""),
      Email: getSocietyField("societyEmail", ""),
    };
    setSelectedBill(receiptObj);
    setViewMode("receipts");
    setModalOpen(true);
  };


  const handleViewLedger = (row) => {
    const ledgerEntries = Array.isArray(items) ? items : [];
    setSelectedBill({
      ledgerEntries,
      UnitNo: row?.unit_no || row?.UnitNo || wingFlat,
      MemberName: row?.member_name || memberName,
      SocietyName: getSocietyField(
        "societyName",
        localSocietyName || "Society",
      ),
      RegistrationNo: getSocietyField("societyRegistrationNo", ""),
      HeaderAddress: getFullAddress(),
      ContactPhone: getSocietyField("contactPersonPhoneNo", ""),
      Email: getSocietyField("societyEmail", ""),
    });
    setViewMode("ledgerDetail");
    setModalOpen(true);
  };


  const getSocietyField = (field, fallback = "") => {
    if (societyData && typeof societyData === "object") {
      const mapping = {
        societyName:
          societyData.societyName ??
          societyData.society_name ??
          societyData.name ??
          societyData.SocietyName,
        societyRegistrationNo:
          societyData.societyRegistrationNo ??
          societyData.society_registration_no ??
          societyData.registrationNo ??
          societyData.RegistrationNo,
        contactPersonPhoneNo:
          societyData.contactPersonPhoneNo ??
          societyData.contactPersonPhone ??
          societyData.contact_phone ??
          societyData.contact_phone_no,
        pinCode:
          societyData.pinCode ??
          societyData.pincode ??
          societyData.pin ??
          societyData.PinCode,
        reraRegistrationNo:
          societyData.reraRegistrationNo ??
          societyData.rera ??
          societyData.rera_registration ??
          "",
        societyAddress:
          societyData.societyAddress ??
          societyData.address_line1 ??
          societyData.address1 ??
          societyData.AddressLine1 ??
          "",
        societyAddress2:
          societyData.societyAddress2 ??
          societyData.address_line2 ??
          societyData.address2 ??
          societyData.AddressLine2 ??
          "",
        societyAddress3:
          societyData.societyAddress3 ??
          societyData.address_line3 ??
          societyData.address3 ??
          societyData.AddressLine3 ??
          "",
        societyArea:
          societyData.societyArea ?? societyData.area ?? societyData.City ?? "",
        societyEmail:
          societyData.societyEmail ??
          societyData.email ??
          societyData.Email ??
          "",
        state:
          societyData.state ?? societyData.stateName ?? societyData.State ?? "",
        societyId:
          societyData.societyId ??
          societyData.id ??
          societyData.SocietyId ??
          null,
      };
      if (field in mapping) return mapping[field] ?? fallback;
      return societyData[field] ?? fallback;
    }


    const ls = {
      societyName: localStorage.getItem("society") || fallback,
      societyRegistrationNo: localStorage.getItem("societyRegNo") || fallback,
      contactPersonPhoneNo:
        localStorage.getItem("contactPersonPhoneNo") || fallback,
      pinCode: localStorage.getItem("pinCode") || fallback,
      societyEmail: localStorage.getItem("societyEmail") || fallback,
    };
    return ls[field] ?? fallback;
  };
  const getFullAddress = () => {
    const a1 = getSocietyField("societyAddress", "");
    const a2 = getSocietyField("societyAddress2", "");
    const a3 = getSocietyField("societyAddress3", "");
    const area = getSocietyField("societyArea", "");
    const state = getSocietyField("state", "");
    const pin = getSocietyField("pinCode", "");
    const parts = [a1, a2, a3, area, state, pin]
      .map((p) => (p || "").toString().trim())
      .filter(Boolean);
    return parts.join(", ");
  };
  function numberToWords(num) {
    if (!num && num !== 0) return "";
    num = Number(num) || 0;
    const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen",];
    const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",];
    if ((num = num.toString()).length > 9) return "Overflow";
    const n = ("000000000" + num)
      .substr(-9)
      .match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
    if (!n) return "";
    let str = "";
    str += n[1] !== "00" ? (a[Number(n[1])] || b[n[1][0]] + " " + a[n[1][1]]) + " Crore " : "";
    str += n[2] !== "00" ? (a[Number(n[2])] || b[n[2][0]] + " " + a[n[2][1]]) + " Lakh " : "";
    str += n[3] !== "00" ? (a[Number(n[3])] || b[n[3][0]] + " " + a[n[3][1]]) + " Thousand " : "";
    str += n[4] !== "0" ? (a[Number(n[4])] || b[n[4][0]] + " " + a[n[4][1]]) + " Hundred " : "";
    str += n[5] !== "00" ? (str !== "" ? "and " : "") + (a[Number(n[5])] || b[n[5][0]] + " " + a[n[5][1]]) + " " : "";
    return str.trim() + " Only";
  }
  const handleDownloadPDF = () => {
    if (!billRef.current) return;
    const html = billRef.current.innerHTML;
    const styles = `
      <style>
        @page { size: A4; margin: 12mm; }
        html, body { font-family: Arial, Helvetica, sans-serif; color: #222; font-size: 12px; }
        table { border-collapse: collapse; width: 100%; }
        .ledger-row { border-bottom: 1px dotted #666; }
        .ledger-small { font-size: 11px; color: #444; margin-top: 4px; }
      </style>
    `;
    const newWindow = window.open("", "_blank", "width=900,height=700");
    if (!newWindow)
      return Swal.fire(
        "Pop-up Blocked",
        "Allow pop-ups to download PDF.",
        "warning",
      );
    newWindow.document.open();
    newWindow.document.write(
      `<html><head><title>Ledger</title>${styles}</head><body>${html}</body></html>`,
    );
    newWindow.document.close();
    setTimeout(() => {
      newWindow.focus();
      newWindow.print();
    }, 400);
  };
  const formatApiAmount = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    const s = String(val).trim();
    const cleaned = s
      .replace(/(Dr|Cr)/gi, "")
      .replace(/,/g, "")
      .trim();
    const num = Number(cleaned);
    if (Number.isNaN(num)) return s;
    const formatted = num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return s.startsWith("-") ? `-${formatted}` : formatted;
  };


  const formatDate = (dateStr) => {
    if (!dateStr) return "-";

    // Case 1: YYYY-MM-DD (ledger)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const date = new Date(dateStr);
      if (isNaN(date)) return "-";

      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }

    // Case 2: DD-MM-YYYY (bills)
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split("-");
      return `${day}/${month}/${year}`;
    }

    return "-";
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === "") return "0.00";
    const cleaned = String(value)
      .replace(/(Dr|Cr)/gi, "")
      .replace(/,/g, "")
      .trim();
    const n = Number(cleaned);
    if (Number.isNaN(n)) return String(value);
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  function computeLedgerRunning(rows) {
    let running = 0;
    return rows.map((r) => {
      const dr = numericVal(r.Debit_Amount);
      const cr = numericVal(r.Credit_Amount);
      running = running + dr - cr;
      const abs = Math.abs(running);
      return {
        ...r,
        __dr: dr,
        __cr: cr,
        __balance: abs,
        __balanceLabel: `${abs.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${running >= 0 ? "Dr" : "Cr"}`,
      };
    });
  }


  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("Token not found");
      return;
    }

    if (
      !commonParams?.wingFlat ||
      !commonParams?.memberName ||
      !commonParams?.societyName
    ) {
      return;
    }

    axios
      .post(
        `${BASE_URL}/ledger/get-ladger-by-member`,
        null,
        {
          params: commonParams,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        console.log("Ledger Response:", response.data);



      })
      .catch((error) => {
        console.error("Error calling ledger API:", error);
      });

    fetchLedger();

  }, [
    commonParams?.wingFlat,
    commonParams?.memberName,
    commonParams?.societyName
  ]);


  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const fetchSocietyByAdmin = async () => {
      setLoadingSociety(true);
      setError("");
      const token = (localStorage.getItem("token") || "").trim();
      const societyName = (localStorage.getItem("society") || "").trim();
      const registered = (localStorage.getItem("societyRegNo") || "").trim();
      if (!token) {
        setError("Missing authentication token. Please log in.");
        setLoadingSociety(false);
        return;
      }
      if (!societyName) {
        setError("Missing society name in localStorage.");
        setLoadingSociety(false);
        return;
      }
      if (!registered) {
        setError("Missing society registration number in localStorage.");
        setLoadingSociety(false);
        return;
      }
      try {
        const response = await axios.post(
          `${BASE_URL}/api/get-society-by-admin`,
          null,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              societyName: societyName,
              societyRegistrationNo: registered,
            },
            validateStatus: (s) => s < 500,
            signal,
          },
        );
        if (response.status === 401) {
          setError("Unauthorized — please log in again");
          setSocietyData(null);
          return;
        }
        if (response.status >= 400) {
          console.error(
            "Society fetch failed:",
            response.status,
            response.data,
          );
          setError(`Failed to fetch society: ${response.status}`);
          setSocietyData(null);
          return;
        }
        const data = response.data;

        setSocietyData(data || null);
      } catch (err) {
        if (axios.isCancel(err)) return;
        console.error("Error fetching society:", err);
        setError("Failed to fetch society info");
        setSocietyData(null);
      } finally {
        setLoadingSociety(false);
      }
    };
    fetchSocietyByAdmin();
    return () => controller.abort();
  }, []);
  useEffect(() => {
    fetchBillsForMember();
  }, []);


  const fetchLedger = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("Token not found");
      return;
    }

    axios.post(
      `${BASE_URL}/ledger/get-ledger-by-admin`,
      null,
      {
        params: commonParams,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => {
        setLedgerList(response.data);

      })
      .catch((error) => {
        console.error("Error fetching ledger:", error);
      });
  };

  useEffect(() => {
    fetchLedger();
  }, []);

  const filteredData = items.filter((item) => {
    if (!searchQuery.trim()) return true;
    const searchLower = searchQuery.toLowerCase();
    return Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchLower),
    );
  });
  const totalItems = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIdx = (page - 1) * pageSize;
  const pageData = filteredData.slice(startIdx, startIdx + pageSize);
  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(1);
  };
  const getBillTotals = (bill) => {
    if (!bill) return { subTotal: 0, interest: 0, arrears: 0, totalAmount: 0 };
    const chargeKeys = Object.keys(bill).filter(
      (key) =>
        !["id", "member_name", "unit_no", "bill_date", "due_date", "bill_no", "Total_Amount", "sub_total", "InterestorLate_Fee", "Interest/Late_Fee", "total_arrears", "total_amount", "area_sqr_ft", "bill_for"
        ].includes(key),
    );
    const subTotal = chargeKeys.reduce((sum, key) => {
      const val = Number(bill[key]);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
    const interest = Number(
      bill["InterestorLate_Fee"] || bill["Interest/Late_Fee"] || 0,
    );

    const totalAmount = bill.total_amount
    return {
      chargeKeys,
      subTotal,
      interest,
      arrears: Number(bill.total_arrears || 0),
      totalAmount,
    };
  };
  const { subTotal, interest, arrears, totalAmount } = getBillTotals(
    selectedBill || {},
  );
  const getViewModeName = () => {
    switch (viewMode) {
      case "bills":
        return "Bills";
      case "credit":
        return "Credit Notes";
      case "debit":
        return "Debit Notes";
      case "receipts":
        return "Receipts";
      case "ledger":
        return "Ledger";
        fetchLedger();
      default:
        return "Documents";
    }
  };
  const getViewModeIcon = () => {
    switch (viewMode) {
      case "bills":
        return <FileText size={20} />;
      case "credit":
        return <CreditCardIcon size={20} />;
      case "debit":
        return <BadgeDollarSign size={20} />;
      case "receipts":
        return <ReceiptText size={20} />;
      case "ledger":
        return <FileSpreadsheet size={20} />;
      default:
        return <FileText size={20} />;
    }
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    switch (viewMode) {
      case "bills":
        fetchBillsForMember();
        break;
      case "credit":
        fetchCreditForMember();
        break;
      case "debit":
        fetchDebitForMember();
        break;
      case "receipts":
        fetchReceiptsForMember();
        break;
      case "ledger":
        fetchLedgerForMember();
        break;
      default:
        fetchBillsForMember();
    }
  };


  function formatMonthYear(date) {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-GB", {
      month: "short",
      year: "numeric",
    });
  }
  const styles = `
    .financial-modern-card {
      background: white;
      border-radius: 16px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.1);
      overflow: hidden;
      margin-top: 24px;
    }
    .financial-title-section {
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .title-icon {
      background: rgba(255,255,255,0.2);
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      backdrop-filter: blur(10px);
    }
    .financial-title-content h2 {
      margin: 0;
      font-size: 24px;
      font-weight: 700;
    }
    .financial-title-content p {
      margin: 4px 0 0 0;
      opacity: 0.9;
      font-size: 14px;
    }
    .header-controls {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }
    .search-container-modern {
      position: relative;
      width: 300px;
    }
    .search-input-modern {
      width: 100%;
      padding: 12px 16px 12px 48px;
      border-radius: 12px;
      border: 2px solid rgba(255,255,255,0.3);
      background: rgba(255,255,255,0.1);
      color: white;
      font-size: 14px;
      backdrop-filter: blur(10px);
      transition: all 0.3s;
    }

    .search-input-modern:focus {
      outline: none;
      border-color: rgba(255,255,255,0.5);
      background: rgba(255,255,255,0.15);
      box-shadow: 0 0 0 3px rgba(255,255,255,0.1);
    }

    .search-input-modern::placeholder {
      color: rgba(255,255,255,0.7);
    }

    .search-icon-modern {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: rgba(255,255,255,0.8);
    }

    .clear-search-modern {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: rgba(255,255,255,0.8);
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: all 0.2s;
    }

    .clear-search-modern:hover {
      background: rgba(255,255,255,0.1);
      color: white;
    }

    .action-buttons-modern {
      display: flex;
      gap: 12px;
    }

    .btn-modern {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s;
      border: 2px solid transparent;
    }

    .btn-modern-primary {
      background: white;
      color: #667eea;
    }

    .btn-modern-primary:hover {
      background: #f8f9fa;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0,0,0,0.15);
    }

    .btn-modern-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }

    .btn-modern-secondary {
      background: rgba(255,255,255,0.1);
      border-color: rgba(255,255,255,0.3);
      color: white;
    }

    .btn-modern-secondary:hover {
      background: rgba(255,255,255,0.2);
      transform: translateY(-2px);
    }

    .spin {
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .member-info-modern {
      background: white;
      padding: 24px;
      border-bottom: 1px solid #e9ecef;
    }

    .member-info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 24px;
    }

    .info-card {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      padding: 20px;
      border-radius: 12px;
      border: 1px solid #dee2e6;
    }

    .info-card-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
    }

    .info-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
    }

    .info-card-header h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #495057;
    }

    .info-card-content p {
      margin: 8px 0 0 0;
      font-size: 14px;
      color: #6c757d;
    }

    .info-card-content strong {
      font-weight: 600;
      color: #343a40;
    }

    .stats-cards {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 16px;
      padding: 24px;
      background: #f8f9fa;
    }

    .stat-card {
      background: white;
      padding: 20px;
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      border: 1px solid #e9ecef;
    }

    .stat-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
    }

    .stat-icon.total {
      background: linear-gradient(135deg, #4e54c8 0%, #8f94fb 100%);
      color: white;
    }

    .stat-icon.pending {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      color: white;
    }

    .stat-icon.paid {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      color: white;
    }

    .stat-icon.items {
      background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
      color: white;
    }

    .stat-value {
      font-size: 24px;
      font-weight: 700;
      color: #343a40;
      margin-bottom: 4px;
    }

    .stat-label {
      font-size: 14px;
      color: #6c757d;
      font-weight: 500;
    }

    .document-tabs-modern {
      display: flex;
      background: white;
      border-bottom: 1px solid #e9ecef;
      overflow-x: auto;
      padding: 0 24px;
    }

    .document-tab-modern {
      padding: 16px 24px;
      border: none;
      background: transparent;
      color: #6c757d;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s;
      display: flex;
      align-items: center;
      gap: 8px;
      white-space: nowrap;
      border-bottom: 3px solid transparent;
      margin-bottom: -1px;
    }

    .document-tab-modern:hover {
      color: #495057;
      background: #f8f9fa;
    }

    .document-tab-modern.active {
      color: #667eea;
      background: white;
      border-bottom-color: #667eea;
      font-weight: 600;
    }

    .document-tab-modern.active svg {
      color: #667eea;
    }

    .tab-count {
      background: #e9ecef;
      color: #495057;
      font-size: 12px;
      font-weight: 600;
      padding: 2px 8px;
      border-radius: 12px;
      margin-left: 4px;
    }

    .document-tab-modern.active .tab-count {
      background: #667eea;
      color: white;
    }

    .table-container-modern {
      padding: 24px;
    }

    .table-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      flex-wrap: wrap;
      gap: 16px;
    }

    .table-title {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .table-title h3 {
      margin: 0;
      font-size: 18px;
      color: #343a40;
    }

    .table-controls {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }

    .entries-selector-modern {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .entries-selector-modern select {
      padding: 8px 12px;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      font-size: 14px;
      background: white;
      cursor: pointer;
      min-width: 100px;
    }

    .entries-selector-modern select:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }

    .sort-buttons {
      display: flex;
      gap: 8px;
    }

    .sort-btn {
      padding: 8px 16px;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      background: white;
      color: #495057;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .sort-btn:hover {
      background: #f8f9fa;
      border-color: #adb5bd;
    }

    .sort-btn.active {
      background: #667eea;
      color: white;
      border-color: #667eea;
    }

    .modern-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }

    .modern-table thead {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }

    .modern-table th {
      padding: 16px;
      font-weight: 600;
      color: white;
      font-size: 13px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      text-align: left;
      border-bottom: 2px solid #5a67d8;
    }

    .modern-table tbody tr {
      transition: all 0.2s;
      border-bottom: 1px solid #e9ecef;
    }

    .modern-table tbody tr:hover {
      background-color: #f8f9fa;
      transform: translateX(4px);
    }

    .modern-table td {
      padding: 16px;
      font-size: 14px;
      color: #495057;
      border-bottom: 1px solid #e9ecef;
    }

    .modern-table td:last-child {
      border-right: none;
    }

    .action-buttons-table {
      display: flex;
      gap: 8px;
      justify-content: center;
    }

    .table-action-btn {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      cursor: pointer;
      transition: all 0.2s;
    }

    .table-action-btn.view {
      background: #dbeafe;
      color: #1d4ed8;
    }

    .table-action-btn.view:hover {
      background: #bfdbfe;
      transform: translateY(-2px);
    }

    .table-action-btn.download {
      background: #dcfce7;
      color: #16a34a;
    }

    .table-action-btn.download:hover {
      background: #bbf7d0;
      transform: translateY(-2px);
    }

    .table-action-btn.print {
      background: #fef3c7;
      color: #d97706;
    }

    .table-action-btn.print:hover {
      background: #fde68a;
      transform: translateY(-2px);
    }

    .amount-cell {
      font-weight: 600;
      font-family: 'Courier New', monospace;
    }

    .positive-amount {
      color: #16a34a;
    }

    .negative-amount {
      color: #dc2626;
    }

    .pagination-modern {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px;
      border-top: 1px solid #e9ecef;
      background: white;
    }

    .pagination-info-modern {
      font-size: 14px;
      color: #6c757d;
    }

    .pagination-controls-modern {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .pagination-btn-modern {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: white;
      border: 1px solid #dee2e6;
      color: #495057;
      cursor: pointer;
      transition: all 0.2s;
    }

    .pagination-btn-modern:hover:not(:disabled) {
      background: #f8f9fa;
      border-color: #adb5bd;
      transform: translateY(-2px);
    }

    .pagination-btn-modern:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .pagination-page-modern {
      padding: 8px 16px;
      border-radius: 8px;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s;
      min-width: 40px;
      text-align: center;
      background: white;
      border: 1px solid #dee2e6;
      color: #495057;
    }

    .pagination-page-modern:hover {
      background: #f8f9fa;
      border-color: #adb5bd;
    }

    .pagination-page-modern.active {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border-color: #667eea;
      font-weight: 600;
    }

    .loading-state-modern {
      text-align: center;
      padding: 80px 20px;
    }

    .loading-icon {
      animation: spin 1s linear infinite;
      margin-bottom: 24px;
    }

    .error-state-modern {
      text-align: center;
      padding: 80px 20px;
      background: #fff5f5;
      border-radius: 12px;
      margin: 24px;
    }

    .error-icon {
      color: #dc2626;
      margin-bottom: 24px;
    }

    .empty-state-modern {
      text-align: center;
      padding: 80px 20px;
      background: #f8f9fa;
      border-radius: 12px;
      margin: 24px;
    }

    .empty-icon {
      color: #9ca3af;
      margin-bottom: 24px;
    }

    @media (max-width: 768px) {
      .financial-modern-container {
        padding: 16px;
      }
      
      // .financial-modern-header {
      //   padding: 16px;
      //   flex-direction: column;
      //   align-items: stretch;
      // }
      
      .search-container-modern {
        width: 100%;
      }
      
      .header-controls {
        width: 100%;
        justify-content: space-between;
      }
      
      .action-buttons-modern {
        width: 100%;
        justify-content: center;
      }
      
      .member-info-grid {
        grid-template-columns: 1fr;
      }
      
      .stats-cards {
        grid-template-columns: repeat(2, 1fr);
      }
      
      .table-header {
        flex-direction: column;
        align-items: stretch;
      }
      
      .table-controls {
        width: 100%;
        justify-content: space-between;
      }
      
      .pagination-modern {
        flex-direction: column;
        gap: 16px;
      }
    }
  `;
  let ledgerRendered = false;

  return (
    <div className="app-container">
      <style>{styles}</style>
      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />
          <div className="financial-modern-container">
            <div className="financial-modern-card">
              <div className="financial-modern-header">
                <div className="header-controls">
                  <div className="search-container-modern">
                    <Search size={20} className="search-icon-modern" />
                    <input
                      type="text"
                      className="search-input-modern"
                      placeholder={`Search ${getViewModeName().toLowerCase()}...`}
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(1);
                      }}
                    />
                    {searchQuery && (
                      <button
                        className="clear-search-modern"
                        onClick={handleClearSearch}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <div className="action-buttons-modern">
                    <button
                      className="btn-modern btn-modern-secondary"
                      onClick={() => {
                        switch (viewMode) {
                          case "bills":
                            return fetchBillsForMember();
                          case "credit":
                            return fetchCreditForMember();
                          case "debit":
                            return fetchDebitForMember();
                          case "receipts":
                            return fetchReceiptsForMember();
                          case "ledger":
                            return fetchLedgerForMember();
                          default:
                            return fetchBillsForMember();
                        }
                      }}
                      disabled={loading}
                    >
                      <RefreshCw size={16} className={loading ? "spin" : ""} />
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
              <div className="document-tabs-modern">
                <button className={`document-tab-modern ${viewMode === "bills" ? "active" : ""}`} onClick={fetchBillsForMember} > <FileText size={16} />
                  <span>Bills</span> <span className="tab-count"> {" "} {viewMode === "bills" ? summaryStats.totalItems : ""}{" "} </span>
                </button>
                <button className={`document-tab-modern ${viewMode === "credit" ? "active" : ""}`} onClick={fetchCreditForMember} >
                  <CreditCardIcon size={16} /> <span>Credit Notes</span> <span className="tab-count"> {viewMode === "credit" ? summaryStats.totalItems : ""}
                  </span>
                </button>
                <button className={`document-tab-modern ${viewMode === "debit" ? "active" : ""}`} onClick={fetchDebitForMember} > {" "}
                  <BadgeDollarSign size={16} /> <span>Debit Notes</span> <span className="tab-count"> {" "} {viewMode === "debit" ? summaryStats.totalItems : ""}{" "}
                  </span>
                </button>
                <button className={`document-tab-modern ${viewMode === "receipts" ? "active" : ""}`} onClick={fetchReceiptsForMember} > {" "}
                  <ReceiptText size={16} /> <span>Receipts</span> <span className="tab-count"> {" "} {viewMode === "receipts" ? summaryStats.totalItems : ""}{" "}
                  </span>
                </button>
                <button className={`document-tab-modern ${viewMode === "ledger" ? "active" : ""}`} onClick={fetchLedgerForMember} > {" "}
                  <FileSpreadsheet size={16} />
                  <span>Ledger</span>
                  <span className="tab-count"> {" "} {viewMode === "ledger" ? summaryStats.totalItems : ""}{" "} </span>
                </button>
              </div>
              <div className="table-container-modern">
                {loading ? (
                  <div className="loading-state-modern"> {" "} <RefreshCw size={48} className="loading-icon" />{" "}
                    <h3>Loading {getViewModeName()}</h3>{" "} <p>Please wait while we fetch the documents...</p>{" "}
                  </div>
                ) : error ? (
                  <div className="error-state-modern"> {" "} <AlertCircle size={48} className="error-icon" />{" "}
                    <h3>Unable to Load Data</h3> <p>{error}</p>
                    <button className="btn-modern btn-modern-primary" onClick={fetchBillsForMember} style={{ marginTop: "16px" }} > {" "} <RefreshCw size={16} /> Try Again{" "} </button>
                  </div>
                ) : totalItems === 0 ? (
                  <div className="empty-state-modern"> {" "} <FileX size={48} className="empty-icon" />{" "} <h3>No {getViewModeName()} Found</h3>{" "}
                    <p>There are no documents available for this member.</p>
                  </div>
                ) : (
                  <>
                    <div className="table-header"> <div className="table-title"> {" "} <h3> {" "} {getViewModeName()} ({totalItems}){" "} </h3>{" "} </div>
                      <div className="table-controls">
                        <div className="entries-selector-modern"> {" "} <span>Show:</span> <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} >
                          <option value="5">5 entries</option>
                          <option value="10">10 entries</option>
                          <option value="25">25 entries</option>
                          <option value="50">50 entries</option>
                        </select>
                        </div>
                        <div className="sort-buttons">
                          <button className={`sort-btn ${sortConfig.key === "date" ? "active" : ""}`} onClick={() => handleSort("date")} > {" "} <Calendar size={14} /> Sort by Date{" "} </button>
                          {viewMode !== "ledger" && (<button className={`sort-btn ${sortConfig.key === "amount" ? "active" : ""}`} onClick={() => handleSort(
                            viewMode === "receipts" ? "amount" : "total_amount",)} > <DollarSign size={14} /> Sort by Amount{" "} </button>)}
                        </div>
                      </div>
                    </div>
                    <div className="table-wrapper">
                      <table className="modern-table">
                        <thead>
                          {viewMode === "bills" && (
                            <tr>
                              <th>Bill No</th>
                              <th>Bill Date</th>
                              <th>Due Date</th>
                              <th>Member Name</th>
                              <th style={{ textAlign: "right" }}> Total Amount </th>
                              <th style={{ textAlign: "center" }}>Actions</th>
                            </tr>
                          )}
                          {viewMode === "credit" && (
                            <tr>
                              <th></th>
                              <th>Date</th>
                              <th>Unit No</th>
                              <th>Member</th>
                              <th style={{ textAlign: "right" }}></th>
                              <th style={{ textAlign: "right" }}></th>
                              <th style={{ textAlign: "center" }}>Actions</th>
                            </tr>
                          )}
                          {viewMode === "debit" && (
                            <tr>
                              <th></th>
                              <th></th>
                              <th>Date</th>
                              <th style={{ textAlign: "right" }}></th>
                              <th style={{ textAlign: "center" }}>Actions</th>
                            </tr>
                          )}
                          {viewMode === "receipts" && (
                            <tr>
                              <th>Receipt No</th>
                              <th>Date</th>
                              <th>Payment Mode</th>
                              <th>Bank/Instrument</th>
                              <th style={{ textAlign: "right" }}>Amount (₹)</th>
                              <th style={{ textAlign: "center" }}>Actions</th>
                            </tr>
                          )}
                          {viewMode === "ledger" && (
                            <tr>
                              <th>Ledger</th>
                              <th></th>
                              <th></th>
                              <th style={{ textAlign: "center" }}>Actions</th>
                            </tr>
                          )}
                        </thead>
                        <tbody>
                          {pageData.map((item, idx) => (
                            <React.Fragment key={idx}>
                              {viewMode === "bills" && (
                                <tr>
                                  <td>{item.bill_no ?? "-"}</td>
                                  <td>{formatDate(item.bill_date)}</td>
                                  <td>{formatDate(item.due_date)}</td>
                                  <td>{item.member_name ?? "-"}</td>
                                  <td className="amount-cell" style={{ textAlign: "right" }} > {" "} <span className="positive-amount"> {" "} ₹{formatApiAmount(item.total_amount)}{" "} </span>{" "} </td>
                                  <td> {" "} <div className="action-buttons-table"> {" "} <button className="table-action-btn view" onClick={() => handleView(item)} title="View bill" > {" "} <Eye size={14} /> </button>
                                  </div>
                                  </td>
                                </tr>
                              )}
                              {viewMode === "credit" && (
                                <tr>
                                  <td>{item.reference ?? "-"}</td>
                                  <td>{formatDate(item.Date)}</td>
                                  <td>{item.unit_no ?? "-"}</td>
                                  <td>{item.Member_Name ?? "-"}</td>
                                  <td className="amount-cell" style={{ textAlign: "right" }} > {item.credit_amount > 0 && (<span className="positive-amount"> {" "} ₹ {formatApiAmount(item.credit_amount,)}{" "} </span>)} </td>
                                  <td className="amount-cell" style={{ textAlign: "right" }} > {item.debit_amount > 0 && (<span className="negative-amount">  {" "} ₹ {formatApiAmount(item.debit_amount,)}{" "} </span>)} </td>
                                  <td> <div className="action-buttons-table"> <button className="table-action-btn view" onClick={() => handleViewCredit(item)} title="View credit note" > {" "} <Eye size={14} /> </button>
                                  </div>
                                  </td>
                                </tr>
                              )}
                              {viewMode === "debit" && (
                                <tr>
                                  <td>{item.bill_no ?? "-"}</td>
                                  <td>{formatDate(item.debit_date)}</td>
                                  <td>{item.reference ?? "-"}</td>
                                  <td className="amount-cell" style={{ textAlign: "right" }} > {" "} <span className="negative-amount"> {" "} ₹ {formatApiAmount(item.debit_amount || item.amount,)}{" "} </span>{" "} </td>
                                  <td> {" "} <div className="action-buttons-table"> {" "}
                                    <button className="table-action-btn view" onClick={() => handleViewDebit(item)} title="View debit note" > {" "} <Eye size={14} /> </button>
                                  </div>{" "}
                                  </td>
                                </tr>
                              )}
                              {viewMode === "receipts" && (
                                <tr>
                                  <td>{item.receipt_no ?? "-"}</td>
                                  <td> {" "} {formatDate(item.receipt_date || item.date,)}{" "} </td>
                                  <td>{item.instrument ?? "Cash"}</td>
                                  <td> {" "} {item.instrument_no ?? item.BankName ?? "-"}{" "} </td>
                                  <td className="amount-cell" style={{ textAlign: "right" }} > {" "} <span className="positive-amount"> {" "} ₹{" "}  {formatApiAmount(item.total_amount
                                    || item.amount,)}{" "} </span>{" "} </td>
                                  <td> {" "} <div className="action-buttons-table"> {" "} <button className="table-action-btn view" onClick={() => handleViewReceipt(item)} title="View receipt" > {" "} <Eye size={14} />{" "} </button>{" "}
                                  </div>{" "}
                                  </td>
                                </tr>
                              )}
                              {viewMode === "ledger" &&
                                !ledgerRendered &&
                                (() => {
                                  ledgerRendered = true;
                                  return (
                                    <tr>
                                      <td>{formatDate(item.memberName)}</td>
                                      <td>{item.balance ?? "-"}</td>
                                      <td>{item.numberBillOrReceipt ?? "-"}</td>
                                      <td>
                                        <div className="action-buttons-table">
                                          <button
                                            className="table-action-btn view"
                                            onClick={() => handleViewLedger(item)}
                                            title="View ledger"
                                          >
                                            <Eye size={14} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })()}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {totalPages > 1 && (
                      <div className="pagination-modern">
                        <div className="pagination-info-modern"> Showing {startIdx + 1} to{" "} {Math.min(startIdx + pageSize, totalItems)} of{" "} {totalItems} {getViewModeName().toLowerCase()}
                        </div>
                        <div className="pagination-controls-modern">
                          <button className="pagination-btn-modern" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} > <ChevronLeft size={16} /> </button>
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (page <= 3) {
                              pageNum = i + 1;
                            } else if (page >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else { pageNum = page - 2 + i; }
                            return (
                              <button key={pageNum} className={`pagination-page-modern ${page === pageNum ? "active" : ""}`} onClick={() => setPage(pageNum)} > {pageNum} </button>
                            );
                          },
                          )}
                          <button className="pagination-btn-modern" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}> <ChevronRight size={16} /> </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          {modalOpen && selectedBill && (
            <div className="modal-overlay edit-modal-overlay" onClick={handleCloseModal}>
              <div className="modal-content edit-modal-content " onClick={(e) => e.stopPropagation()} style={{ maxWidth: "900px", width: "90%", maxHeight: "90vh", backgroundColor: "#f5f5f5" }} >
                <div className="edit-modal-header">
                  <h3 className="edit-modal-title">
                    {viewMode === "billDetail" && <FileText size={20} />}
                    {viewMode === "credit" && <CreditCardIcon size={20} />}
                    {viewMode === "debit" && <BadgeDollarSign size={20} />}
                    {viewMode === "receipts" && <ReceiptText size={20} />}
                    {viewMode === "ledgerDetail" && (<FileSpreadsheet size={20} />)}
                    {viewMode === "billDetail" ? "Maintenance Bill" : viewMode === "credit" ? "Credit Note" : viewMode === "debit" ? "Debit Note" : viewMode === "receipts" ? "Receipt" : "Ledger"}
                  </h3>
                  <div style={{ display: "flex", gap: "12px" }}>
                    <button className="btn-modern btn-modern-primary" onClick={handleDownloadPDF} title="Download PDF">
                      <Download size={16} /> Download{" "} </button>
                    <button className="btn-modern btn-modern-secondary" onClick={() => window.print()} title="Print" > <Printer size={16} /> Print  </button>
                    <button className="edit-modal-close" onClick={handleCloseModal} style={{ background: "none", border: "none", cursor: "pointer", padding: "8px", }} > <X size={20} /> </button>
                  </div>
                </div>
                <div className="edit-modal-body" style={{ overflow: "auto" }}>
                  <div ref={billRef} style={{ padding: "16px" }}>
                    {viewMode === "ledgerDetail" ? (
                      <div className="ledger-container">
                        <div style={{ textAlign: "center", width: "100%" }}>
                          <div className="ledger-header">
                            <b> {selectedBill?.SocietyName || getSocietyField("societyName", localSocietyName)}</b> </div>
                          <div className="header-sub">
                            Registration No {getSocietyField("societyRegistrationNo", "")}
                          </div>
                          <div className="header-sub">
                            {getFullAddress()}
                          </div>
                          {(() => {
                            const today = new Date();
                            const year = today.getFullYear();
                            const month = today.getMonth(); // 0 = Jan, 3 = April

                            const startYear = month >= 3 ? year : year - 1;
                            const endYear = startYear + 1;
                            return (
                              <p className="financial-year">
                                For The Year {startYear}-{endYear}
                              </p>
                            );
                          })()}


                          <p className="print-date">
                            Page: 1 | {new Date().toLocaleDateString()}
                          </p>
                        </div>

                        <div className="member-section">
                          <p>
                            <strong>Unit No:</strong> {selectedBill?.UnitNo} |{" "}
                            <strong>Member:</strong> {selectedBill?.MemberName}
                          </p>
                        </div>
                        <div style={{ overflowX: "auto", marginTop: 10 }}>
                          {(() => {
                            const rawRows = Array.isArray(selectedBill?.ledgerEntries,)
                              ? selectedBill.ledgerEntries : [];
                            const normalized = rawRows.map((r) => {
                              return {
                                date:
                                  r.date ??
                                  r.date ??
                                  r.transactionDate ??
                                  r.instrument_date ??
                                  null,
                                referenceType:
                                  r.referenceType ??
                                  r.Reference ??
                                  r.Type ??
                                  (r.type === "BILL"
                                    ? "Bill"
                                    : r.referenceType) ??
                                  "",
                                referenceNo:
                                  r.referenceNo ??
                                  r.reference_no ??
                                  r.bill_no ??
                                  r.BillNo ??
                                  "",
                                narration:
                                  r.narration ??
                                  r.narration ??
                                  r.remarks ??
                                  r.bill_for ??
                                  r.description ??
                                  "",
                                chequeNo:
                                  r.instrument ??
                                  r.cheque_no ??
                                  r.instrument_no ??
                                  "",
                                chequeDate:
                                  r.instrument ??
                                  r.cheque_date ??
                                  r.instrument_date ??
                                  null,
                                Debit_Amount: numericVal(
                                  r.debit ?? r.Debit ?? r.__dr ?? 0,
                                ),
                                Credit_Amount: numericVal(
                                  r.credit ?? r.credit ?? r.__cr ?? 0,
                                ),
                                totalAmount: numericVal(
                                  r.total ?? r.total ?? toggleSidebar.total,
                                ),
                                raw: r,
                                advancePayment:
                                  numericVal(
                                    r.advancePayment
                                  ),
                                row: r,

                              };
                            });

                            const withBalances =

                              computeLedgerRunning(normalized);
                            function formatAmountForBill(v) {
                              if (v === null || v === undefined || v === "")
                                return "0.00";
                              const cleaned = String(v)
                                .replace(/(Dr|Cr)/gi, "")
                                .replace(/,/g, "")
                                .trim();
                              const n = Number(cleaned);
                              if (Number.isNaN(n)) return String(v);
                              return n.toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              });
                            }
                            return (
                              <table className="charges-table">
                                <thead>
                                  <tr>
                                    <th className="th th-date">DATE</th>
                                    <th className="th">REFERENCE<br /><span className="small-text">TYPE / NO / NARRATION</span></th>
                                    <th className="th th-cheque">Payment Mode / Transaction ID</th>
                                    <th className="th th-dr">DR AMOUNT</th>
                                    <th className="th th-cr">CR AMOUNT</th>
                                    <th className="th th-balance">Pending BALANCE</th>
                                    <th className="th th-balance">ADVANCE BALANCE</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.length === 0 ? (
                                    <tr>
                                      <td colSpan={6} className="no-data">
                                        No ledger entries found.
                                      </td>
                                    </tr>
                                  ) : (
                                    rows
                                      .filter((row, index, self) => {
                                        if (row.referenceType !== "Opening Balance") return true;
                                        return (
                                          index ===
                                          self.findIndex(r =>
                                            r.referenceType === "Opening Balance" &&
                                            getMonthYearKey(r.date) === getMonthYearKey(row.date)
                                          )
                                        );
                                      })
                                      .sort((a, b) => {
                                        if (a.referenceType === "Opening Balance") return -1;
                                        if (b.referenceType === "Opening Balance") return 1;
                                        return 0; // keep everything else same order
                                      })
                                      .map((r, i) => (
                                        <tr key={i} className="ledger-row">
                                          <td>{r.date ? formatMonthYear(r.date) : "-"}</td>

                                          <td>
                                            <div className="ref-type">
                                              {r.referenceType} {r.referenceNo}
                                            </div>
                                            {r.narration && (
                                              <div className="narration">{r.narration}</div>
                                            )}
                                          </td>

                                          <td>
                                            <div>
                                              {r?.instrument || r?.instrumentNo
                                                ? `${r.instrument || ""}${r.instrument && r.instrumentNo ? " / " : ""}${r.instrumentNo || ""}`
                                                : ""}
                                            </div>
                                            {/* <div className="cheque-date">
                                              {r.chequeDate ? formatMonthYear(r.chequeDate) : ""}
                                            </div> */}
                                          </td>

                                          <td className="right">
                                            {Number(r.__dr || 0) ? formatMoney(Number(r.__dr)) : "0.00"}
                                          </td>

                                          <td className="right">
                                            {Number(r.__cr || 0) ? formatMoney(Number(r.__cr)) : "0.00"}
                                          </td>
                                          <td className="right font-bold">
                                            {formatMoney(r.totalAmount)}
                                          </td>
                                          <td className="right">{formatMoney(r.advancePayment)}</td>
                                        </tr>
                                      ))
                                  )}
                                </tbody>
                              </table>
                            );
                          })()}
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18}} >
                          <div style={{ textAlign: "center" }}>Prepared by</div>
                          <div style={{ textAlign: "center" }}> Authorised by </div>
                        </div>
                      </div>
                    ) : viewMode === "receipts" ? (
                      <div style={{ padding: 12 }}>
                        <div style={{ textAlign: "center", marginBottom: 8 }}>
                          <div style={{ fontWeight: 800, fontSize: 18 }}> {selectedBill.SocietyName} </div>
                          <div style={{ fontSize: 12, color: "#444" }}> Registration No {selectedBill.RegistrationNo} </div>
                          <div style={{ fontSize: 12, color: "#444" }}> {selectedBill.HeaderAddress} </div>
                          <div style={{ fontSize: 12, color: "#444" }}>
                            {selectedBill.ContactPhone ? `Ph: ${selectedBill.ContactPhone}` : getSocietyField("contactPersonPhoneNo", "",)}{" "} &nbsp; | &nbsp;{" "}
                            {selectedBill.Email || getSocietyField("societyEmail", "")} </div>
                          {/* <h3 style={{ marginTop: 10, textDecoration: "underline", }} > RECEIPT </h3> */}
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, }} >
                          <div> <strong>Receipt No. :</strong>{" "} {selectedBill.ReceiptNo} </div>
                          <div> <strong>Date :</strong> {selectedBill.Date} </div>
                        </div>
                        <div style={{ marginTop: 12 }}> <p> <strong>Received with Thanks From :</strong>{" "} {selectedBill.MemberName} </p>
                          <p> <strong>Unit No. :</strong> {selectedBill.UnitNo} </p>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, }} >
                          <div style={{ fontSize: 14 }}> <strong>₹</strong> </div>
                          <div style={{ fontSize: 28, fontWeight: 700 }}> {selectedBill.Amount.toLocaleString("en-IN", { minimumFractionDigits: 2, })}
                          </div>
                        </div>
                        <div style={{ marginTop: 8 }}> <p> <strong>Sum of</strong> ( {selectedBill.AmountWords}{" "} ) </p></div>

                        {/* <div style={{ marginTop: 8 }}> <p>  &nbsp;&nbsp; <strong>Payment Mode :</strong>{" "}
                          {selectedBill.InstrumentDate} &nbsp;&nbsp; <strong>Online :</strong> {selectedBill.Online} </p>
                          {selectedBill.DrawnOn && (<p> <strong>Drawn on</strong> : {selectedBill.DrawnOn} </p>)} </div> */}
                          <div style={{ marginTop: 8 }}>
                            <p>
                              &nbsp;&nbsp;
                              <strong>Payment Mode :</strong> {selectedBill.InstrumentDate}
                              &nbsp;&nbsp;

                              <strong>Online :</strong>{" "}
                              {(selectedBill.InstrumentDate?.toLowerCase() === "cheque" ||
                                selectedBill.InstrumentDate?.toLowerCase() === "cash")
                                ? "No"
                                : "Yes"}
                            </p>

                            {selectedBill.DrawnOn && (
                              <p>
                                <strong>Drawn on</strong> : {selectedBill.DrawnOn}
                              </p>
                            )}
                          </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 40, }} >
                          <div style={{ textAlign: "center" }}>
                            <div>For {selectedBill.SocietyName}</div>
                            <div style={{ marginTop: 24 }}> Authorised Signatory </div>
                          </div>
                        </div>
                        <div style={{ marginTop: 16, color: "#444" }}>
                          {selectedBill.Note}
                        </div>
                      </div>
                    ) : viewMode === "debit" ? (
                      <div className="bill-container debit-note">
                        <div className="bill-header-center">
                          <div className="society-name" style={{ textAlign: "center", fontWeight: "bold", fontSize: "22px", width: "100%", display: "block", textTransform: "uppercase", }} >
                            {selectedBill.SocietyName} </div>
                          <div className="sub-detail"> Registration No {selectedBill.RegistrationNo} </div>
                          <div className="sub-detail" style={{ textAlign: "center" }} > {" "} {selectedBill.HeaderAddress || getFullAddress()} </div>
                          <div className="divider" />
                          <div className="note-title debit-title" style={{ textAlign: "center", fontWeight: "bold", fontSize: "18px", width: "100%", marginTop: "10px", }} > DEBIT NOTE </div>
                        </div>
                        <div className="voucher-info-row">
                          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                            <div><strong>Voucher No:</strong> {selectedBill.VoucherNo}</div>
                            <div><strong>Date:</strong> {selectedBill.Date}</div>
                            <div><strong>Member Name:</strong> {selectedBill.MemberName}</div>
                            <div><strong>Unit No:</strong> {selectedBill.UnitNo}</div>
                          </div>
                        </div>
                        <table className="charges-table text-center charges-table">
                          <thead>
                            <tr>
                              <th style={{ color: "#000" }} className="text-center charges-table" > Particulars (Debit) </th>
                              <th style={{ color: "#000" }} className="text-center charges-table" > Amount (Dr) </th>
                              {/* <th style={{ color: "#000" }} className="text-center charges-table" > Amount (Cr) </th> */}
                            </tr>
                          </thead>
                          <tbody>
                            {selectedBill.particulars.map((p, idx) => (
                              <tr key={idx}>
                                <td className="text-left min-h-36">{p.desc}</td>
                                <td className="text-right"> {formatApiAmount(p.amount || p.dr)} </td>
                              </tr>
                            ))}
                            <tr className="totals-row">
                              <td className="text-italic"> ({numberToWords(selectedBill.totalDr)}) </td>
                              <td className="text-right total-bold"> {formatApiAmount(selectedBill.totalDr)} </td>
                            </tr>
                          </tbody>
                        </table>

                        <div className="narration-box">
                          <strong>Narration:</strong> {selectedBill.narration}
                        </div>
                        <div
                          className="signature-section"
                          style={{ display: "flex", justifyContent: "space-between" }}
                        >
                          <div className="sign-box">Prepared by</div>
                          <div className="sign-box">Authorised by</div>
                        </div>
                      </div>
                    ) : viewMode === "credit" ? (
                      <div className="bill-container credit-note">
                        <div className="bill-header-center">
                          <div
                            className="society-name" style={{ textAlign: "center", fontWeight: "bold", fontSize: "22px", width: "100%", display: "block", textTransform: "uppercase", }} >
                            {localStorage.getItem("society") ||
                              getSocietyField("societyName", localSocietyName || "Society")}

                          </div>
                          <div
                            className="sub-detail"
                            style={{
                              textAlign: "center",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <div>{selectedBill.RegistrationNo}</div>

                            <p style={{ margin: "4px 0", color: "#444" }}>
                              Registration No.{" "}
                              {selectedBill.RegistrationNo ||
                                getSocietyField("societyRegistrationNo", "—")}
                            </p>
                          </div>
                          <div className="sub-detail" style={{ textAlign: "center" }} > {" "} {selectedBill.HeaderAddress || getFullAddress()} </div>
                          <div className="divider" />
                          <div className="note-title debit-title" style={{ textAlign: "center", fontWeight: "bold", fontSize: "18px", width: "100%", marginTop: "10px", }} > CREDIT NOTE </div>
                        </div>
                        <div className="voucher-info-row">
                          <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                            <div><strong>Date:</strong> {selectedBill.Date}</div>
                            <div><strong>Member Name:</strong> {selectedBill.MemberName}</div>
                            <div><strong>Unit No:</strong> {selectedBill.UnitNo}</div>
                          </div>
                          <div className="divider" /> <div className="note-title credit-title"></div>
                        </div>
                        <table className="charges-table text-center charges-table">
                          <thead>
                            <tr>
                              <th className="w-70">Particulars (Credit)</th>
                              <th className="w-70">Credit Amount</th>

                            </tr>
                          </thead>
                          <tbody>
                            {selectedBill.particulars.map((p, idx) => (
                              <tr key={idx}>
                                <td className="text-left min-h-36">{p.desc}</td>

                                {/* Credit Column */}
                                <td className="text-right">
                                  {p.cr ? formatApiAmount(p.cr) : "-"}
                                </td>

                                {/* Debit Column */}

                              </tr>
                            ))}
                            <tr className="totals-row">
                              <td className="text-italic">
                                ({numberToWords(selectedBill.totalCr)})
                              </td>
                              <td className="text-right total-bold">
                                {formatApiAmount(selectedBill.totalCr)}
                              </td>
                            </tr>
                          </tbody>
                        </table>

                        <div className="narration-box">
                          <strong>Narration:</strong> {selectedBill.narration}
                        </div>
                        <div className="signature-section" style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
                          <div className="sign-box">Prepared by</div>
                          <div className="sign-box">Authorised by</div>
                        </div>
                      </div>
                    ) : (
                      <div className="bill-container">
                        <div className="bill-header">
                          <h2 style={{ margin: 0 }}>
                            {selectedBill.SocietyName || getSocietyField("societyName", localSocietyName || "Society",)} </h2>
                          <p style={{ margin: "4px 0", color: "#444" }}> Registration No.{" "} {selectedBill.RegistrationNo || getSocietyField("societyRegistrationNo", "—")} </p>
                          <div style={{ fontSize: 12, color: "#444" }}> {getFullAddress()} </div>
                        </div>

                        <div className="bill-info">
                          <div> {" "}
                            <p> <strong>Name:</strong>{" "} {selectedBill.member_name}{" "} </p>
                            <p> {" "} <strong>Flat No.:</strong>{" "} {selectedBill.unit_no}{" "} </p>
                            <p> <strong>Bill No.:</strong>{" "} {selectedBill.bill_no ?? ""}{" "} </p>
                          </div>
                          <div>
                            <p> <strong>Bill Date:</strong>{" "} {selectedBill.bill_date ?? ""}{" "} </p>
                            <p> {" "} <strong>Area (Sq.Ft.):</strong>{" "} {selectedBill.area_sqr_ft ?? "—"}{" "} </p>
                            <p> {" "} <strong>Due Date:</strong>{" "} {selectedBill.due_date ?? "-"}{" "} </p>
                          </div>
                          <div>
                            <p> <strong>Bill For:</strong>{" "} {selectedBill.bill_for ?? "-"} </p>
                          </div>
                        </div>
                        <table className="charges-table text-center charges-table">
                          <thead>
                            <tr>
                              {/* <th style={{ color: "#000" }} className="text-center charges-th" > {" "} </th> */}
                              <th style={{ color: "#000" }} className="text-center charges-table" > {" "} Particulars of Charges </th>
                              <th style={{ color: "#000" }} className="text-center charges-table" > {" "}  Amount (₹){" "} </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const chargeKeys = Object.keys(selectedBill,).filter(
                                (key) =>
                                  !["id", "member_name", "unit_no", "bill_date", "due_date", "bill_no", "total_amount", "bill_for", "area_sqr_ft", "is_previous_year"].includes(key),);
                              return chargeKeys.map((key, idx) => (
                                <tr key={key}>
                                  {/* <td className="text-center border"> {" "} {idx + 1} </td> */}
                                  <td className="text-center border"> {" "} {key.replace(/_/g, " ")}{" "} </td>
                                  <td className="text-center border"> {" "} {selectedBill[key]}{" "} </td>
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                        <div className="totals-section">
                          <p> {" "} <strong> Rupees {formatAmountForBill(totalAmount)}{" "} Only{" "} </strong></p>
                          <table style={{ width: "100%", marginTop: 6 }}>
                            <tbody>
                              <tr>
                                <td>Total</td>
                                <td style={{ textAlign: "right" }}>{" "} ₹ {formatAmountForBill(subTotal)} </td>
                              </tr>
                              <tr>
                                <td>Add: Interest</td>
                                <td style={{ textAlign: "right" }}> {" "} ₹ {formatAmountForBill(interest)}{" "} </td>
                              </tr>
                              <tr>
                                <td>Less: Adjustment</td>
                                <td style={{ textAlign: "right" }}>₹ 0.00</td>
                              </tr>
                              <tr>
                                <td>Arrears</td>
                                <td style={{ textAlign: "right" }}> ₹ {formatAmountForBill(arrears)} </td>
                              </tr>
                              <tr className="grand-total" style={{ fontWeight: 700 }} >
                                <td> <strong>Amount Due</strong> </td>
                                <td style={{ textAlign: "right" }}> <strong> ₹ {formatAmountForBill(totalAmount)} </strong>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="notes" style={{ marginTop: 12, color: "#444" }}>
                          {notes.map((item, index) => (
                            <p key={index} style={{ margin: 4 }}>
                              {index + 1}. {item.note}
                            </p>
                          ))}
                          <p style={{ margin: 4 }}>
                            {notes.length + 4}. Contact: {getSocietyField("contactPersonPhoneNo", "")} or email: {getSocietyField("societyEmail", "")}
                          </p>

                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
export default DownloadPDF;
